// @flow
import React from 'react';
import anime from 'animejs';
import { Box } from 'gestalt';
import BrowserWindowSvg, {
  BROWSER_WINDOW_TRANSLATE_Y,
  PINTEREST_LOGO_BG_RADIUS_X,
  PINTEREST_LOGO_BG_RADIUS_Y,
  PINTEREST_LOGO_RADIUS,
  PINTEREST_LOGO_SCALE,
  PINTEREST_LOGO_TRANSLATE_X,
  PINTEREST_LOGO_TRANSLATE_Y,
} from 'app/common/react/components/growth/activation/ExtensionUpsell/BrowserWindowSvg';

type Props = {|
  isLooping: boolean,
|};

const calcScaledTranslation = (originalTranslate: number, bBoxWidth: number, scaleTo: number) =>
  originalTranslate - (bBoxWidth / 2) * (scaleTo - 1);

// Animation constants
const MORPH_DOT_SIZE = 30;
const PINTEREST_LOGO_SCALE_TO = 1.4;
const PINTEREST_LOGO_TRANSLATE_X_SCALED = calcScaledTranslation(
  PINTEREST_LOGO_TRANSLATE_X,
  PINTEREST_LOGO_RADIUS * 2,
  PINTEREST_LOGO_SCALE_TO,
);
const PINTEREST_LOGO_TRANSLATE_Y_SCALED = calcScaledTranslation(
  PINTEREST_LOGO_TRANSLATE_Y,
  PINTEREST_LOGO_RADIUS * 2,
  PINTEREST_LOGO_SCALE_TO,
);
const PINTEREST_LOGO_BG_SCALE = 1;
const PINTEREST_LOGO_BG_SCALE_TO = 2.1;
const PINTEREST_LOGO_BG_TRANSLATE_X = 279;
const PINTEREST_LOGO_BG_TRANSLATE_Y = 2;
const PINTEREST_LOGO_BG_TRANSLATE_X_SCALED = calcScaledTranslation(
  PINTEREST_LOGO_BG_TRANSLATE_X,
  PINTEREST_LOGO_BG_RADIUS_X * 2,
  PINTEREST_LOGO_BG_SCALE_TO,
);
const PINTEREST_LOGO_BG_TRANSLATE_Y_SCALED = calcScaledTranslation(
  PINTEREST_LOGO_BG_TRANSLATE_Y,
  PINTEREST_LOGO_BG_RADIUS_Y * 2,
  PINTEREST_LOGO_BG_SCALE_TO,
);

const FADE_IN_WINDOW_DURATION = 500;
const FADE_IN_CONTENT_DURATION = 500;
const SHRINK_CONTENT_DURATION = 300;
const MOVE_DOT_DURATION = 300;
const POP_LOGO_BIG_DURATION = 280;
const POP_LOGO_BG_BIG_DURATION = 210;
const POP_LOGO_SMALL_DURATION = 190;
const POP_LOGO_BG_SMALL_DURATION = 300;
const HOLD_END_DURATION = 1400;

const NO_LOOP_DELAY = 750;

// Animation properties
const fadeInWindow = {
  targets: '.simplePopExtensionContent',
  opacity: [0, 1],
  duration: FADE_IN_WINDOW_DURATION,
  easing: 'easeInOutQuad',
};

const fadeInContent = {
  targets: ['#content-morph', '.content-bar'],
  opacity: [0, 1],
};

const shrinkContent = {
  targets: '#content-morph',
  width: MORPH_DOT_SIZE,
  height: MORPH_DOT_SIZE,
  x: 220,
  y: 203,
  rx: 20,
};

const shrinkBars = {
  targets: '.content-bar',
  width: 0,
  height: 0,
  x: 200 + MORPH_DOT_SIZE / 2,
  y: 203 + MORPH_DOT_SIZE / 2,
};

const moveDot = {
  targets: '#content-morph',
  x: 316 - MORPH_DOT_SIZE / 2,
  y: 39 - MORPH_DOT_SIZE / 2,
};

const popLogoBig = {
  targets: '#pinterest-logo',
  translateX: [PINTEREST_LOGO_TRANSLATE_X, PINTEREST_LOGO_TRANSLATE_X_SCALED],
  translateY: PINTEREST_LOGO_TRANSLATE_Y_SCALED,
  scale: PINTEREST_LOGO_SCALE_TO,
  direction: 'alternate',
};

const popLogoSmall = {
  targets: '#pinterest-logo',
  translateX: [PINTEREST_LOGO_TRANSLATE_X_SCALED, PINTEREST_LOGO_TRANSLATE_X],
  translateY: PINTEREST_LOGO_TRANSLATE_Y,
  scale: PINTEREST_LOGO_SCALE,
  direction: 'alternate',
};

const popLogoBgBig = {
  targets: '#pinterest-logo-pop-bg',
  translateX: [PINTEREST_LOGO_BG_TRANSLATE_X, PINTEREST_LOGO_BG_TRANSLATE_X_SCALED],
  translateY: [PINTEREST_LOGO_BG_TRANSLATE_Y, PINTEREST_LOGO_BG_TRANSLATE_Y_SCALED],
  scale: PINTEREST_LOGO_BG_SCALE_TO,
  direction: 'alternate',
};

const popLogoBgSmall = {
  targets: '#pinterest-logo-pop-bg',
  translateX: [PINTEREST_LOGO_BG_TRANSLATE_X_SCALED, PINTEREST_LOGO_BG_TRANSLATE_X],
  translateY: [PINTEREST_LOGO_BG_TRANSLATE_Y_SCALED, PINTEREST_LOGO_BG_TRANSLATE_Y],
  scale: PINTEREST_LOGO_BG_SCALE,
};

const createSimplePopTimeline = (isLooping: boolean) => {
  const shrinkContentOffset = FADE_IN_CONTENT_DURATION + (isLooping ? 0 : NO_LOOP_DELAY);
  const popLogoBigOffset = shrinkContentOffset + SHRINK_CONTENT_DURATION;
  const popLogoBigBgOffset = popLogoBigOffset;
  const popLogoSmallOffset = popLogoBigOffset + POP_LOGO_BIG_DURATION;
  const popLogoBgSmallOffset = popLogoBigBgOffset + POP_LOGO_BG_BIG_DURATION;

  const simplePopTimeline = anime.timeline({
    easing: 'easeInOutQuad',
    loop: isLooping,
    direction: 'normal',
    autoplay: false,
  });

  simplePopTimeline.add({ ...fadeInContent, duration: FADE_IN_CONTENT_DURATION });

  if (!isLooping) {
    simplePopTimeline.add({ duration: NO_LOOP_DELAY });
  }

  simplePopTimeline
    .add(
      {
        ...shrinkContent,
        duration: SHRINK_CONTENT_DURATION,
      },
      shrinkContentOffset,
    )
    .add({ ...shrinkBars, duration: SHRINK_CONTENT_DURATION }, shrinkContentOffset)
    .add({ ...moveDot, duration: MOVE_DOT_DURATION }, popLogoBigOffset)
    .add({ ...popLogoBig, duration: POP_LOGO_BIG_DURATION }, popLogoBigOffset)
    .add({ ...popLogoBgBig, duration: POP_LOGO_BG_BIG_DURATION }, popLogoBigBgOffset)
    .add(
      {
        ...popLogoSmall,
        duration: POP_LOGO_SMALL_DURATION,
        easing: 'cubicBezier(0, 0, 0.23, 0.8)',
      },
      popLogoSmallOffset,
    )
    .add(
      {
        ...popLogoBgSmall,
        duration: POP_LOGO_BG_SMALL_DURATION,
      },
      popLogoBgSmallOffset,
    )
    .add({ duration: HOLD_END_DURATION });

  return simplePopTimeline;
};

class ExtensionContentSimplePop extends React.PureComponent<Props> {
  static defaultProps = { isLooping: false };

  componentDidMount() {
    const { isLooping } = this.props;

    window.requestAnimationFrame(() => {
      const timeline = createSimplePopTimeline(isLooping);
      anime(fadeInWindow).finished.then(() => timeline.play());
    });
  }

  render() {
    return (
      <Box
        display="flex"
        flex="grow"
        justifyContent="center"
        alignItems="center"
        marginTop={Math.floor(-BROWSER_WINDOW_TRANSLATE_Y / 4)}
      >
        <BrowserWindowSvg />
      </Box>
    );
  }
}
export default ExtensionContentSimplePop;
